"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InactivityWarningModal = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * InactivityWarningModal - Warns user before automatic logout
 *
 * Accessibility features:
 * - Focus trap when modal opens
 * - Keyboard navigation (Tab, Escape)
 * - ARIA attributes for screen readers
 * - High contrast warning colors
 */
const react_1 = require("react");
const InactivityProvider_1 = require("../providers/InactivityProvider");
require("./InactivityWarningModal.scss");
const InactivityWarningModal = () => {
    const { isWarningShown, timeRemaining, continueSession } = (0, InactivityProvider_1.useInactivity)();
    const modalRef = (0, react_1.useRef)(null);
    const continueButtonRef = (0, react_1.useRef)(null);
    /**
     * Focus the continue button when modal opens (accessibility)
     */
    (0, react_1.useEffect)(() => {
        if (isWarningShown && continueButtonRef.current) {
            continueButtonRef.current.focus();
        }
    }, [isWarningShown]);
    /**
     * Handle Escape key to continue session
     */
    (0, react_1.useEffect)(() => {
        if (!isWarningShown)
            return;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                continueSession();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isWarningShown, continueSession]);
    /**
     * Prevent background scrolling when modal is open
     */
    (0, react_1.useEffect)(() => {
        if (isWarningShown) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = '';
            };
        }
    }, [isWarningShown]);
    if (!isWarningShown) {
        return null;
    }
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    const timeDisplay = minutes > 0
        ? `${minutes}:${seconds.toString().padStart(2, '0')}`
        : `${seconds} seconds`;
    return ((0, jsx_runtime_1.jsx)("div", { className: "inactivity-warning-overlay", role: "dialog", "aria-modal": "true", "aria-labelledby": "inactivity-title", "aria-describedby": "inactivity-description", ref: modalRef, children: (0, jsx_runtime_1.jsxs)("div", { className: "inactivity-warning-modal", children: [(0, jsx_runtime_1.jsx)("div", { className: "inactivity-warning-icon", "aria-hidden": "true", children: "\u26A0\uFE0F" }), (0, jsx_runtime_1.jsx)("h2", { id: "inactivity-title", className: "inactivity-warning-title", children: "Session Expiring Soon" }), (0, jsx_runtime_1.jsxs)("p", { id: "inactivity-description", className: "inactivity-warning-message", children: ["You've been inactive for a while. For your security, your session will automatically end in ", (0, jsx_runtime_1.jsx)("strong", { children: timeDisplay }), " unless you choose to continue."] }), (0, jsx_runtime_1.jsx)("div", { className: "inactivity-warning-actions", children: (0, jsx_runtime_1.jsx)("button", { ref: continueButtonRef, className: "btn btn-primary", onClick: continueSession, "aria-label": "Continue your session and stay logged in", children: "Stay Logged In" }) }), (0, jsx_runtime_1.jsx)("p", { className: "inactivity-warning-note", children: "Click anywhere or press any key to stay active" })] }) }));
};
exports.InactivityWarningModal = InactivityWarningModal;
